import axios from 'axios';
import { getJson, sendJson } from '@/lib/api';
import { authHeader } from '@/lib/events';

export const STAFF_SOURCES = ['facilitator', 'wingteam', 'bce'] as const;
export type StaffSource = (typeof STAFF_SOURCES)[number];
export const STAFF_SOURCE_LABELS: Readonly<Record<StaffSource, string>> = { facilitator: 'Facilitator', wingteam: 'WingTeam', bce: 'BCE' };

export const ASSISTANT_ROLES = ['assistant', 'guitar_tech', 'co_facilitator', 'other'] as const;
export type AssistantRole = (typeof ASSISTANT_ROLES)[number];
export const ASSISTANT_ROLE_LABELS: Readonly<Record<AssistantRole, string>> = { assistant: 'Assistant', guitar_tech: 'Guitar tech', co_facilitator: 'Co-facilitator', other: 'Other' };

export const ASSISTANT_STATUSES = ['requested', 'confirmed', 'canceled'] as const;
export type AssistantStatus = (typeof ASSISTANT_STATUSES)[number];

export const PAY_BASIS = { hour: 'hour', flat: 'flat' } as const;
export type PayBasis = (typeof PAY_BASIS)[keyof typeof PAY_BASIS];
export const PAY_BASIS_LABELS: Readonly<Record<PayBasis, string>> = { flat: 'Flat', hour: 'Per hour' };

/** "$25/hr", "$150 flat", or null when there is no rate. */
export function formatPay(payRate: number | null | undefined, payBasis: PayBasis | null | undefined): string | null {
  if (payRate === null || payRate === undefined) return null;
  return `$${payRate.toLocaleString('en-US')}${payBasis === PAY_BASIS.hour ? '/hr' : payBasis === PAY_BASIS.flat ? ' flat' : ''}`;
}

/** Dollars as payroll reads them: "$1,350" or "$1,350.50". */
export function formatDollars(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(amount) ? 0 : 2 });
}

export interface StaffContact {
  readonly _id: string;
  readonly name: string;
  readonly email?: string;
  readonly phone?: string;
  readonly city?: string;
  readonly state?: string;
  readonly skills: readonly string[];
  readonly notes?: string;
  readonly isActive: boolean;
  readonly source: StaffSource;
  readonly updatedAt?: string;
}
export type StaffContactWrite = Partial<Omit<StaffContact, '_id' | 'updatedAt'>> & { readonly name: string };

/** Who an assistant is: a cloud-only staff contact, or a team member with a login. */
export type PersonKind = 'contact' | 'user';

export interface EventAssistant {
  readonly _id: string;
  readonly staffContactId?: string;
  readonly userId?: string;
  /** The assistant category whose defaults were applied, if any. */
  readonly categoryId?: string | null;
  readonly contact?: { readonly _id: string; readonly kind?: PersonKind; readonly name: string; readonly phone?: string; readonly email?: string; readonly city?: string; readonly state?: string };
  readonly role: AssistantRole;
  readonly callTime?: string;
  readonly endTime?: string;
  /** Internal roles only; absent for contractors. */
  readonly payRate?: number | null;
  readonly payBasis?: PayBasis;
  readonly parkingNeeded?: boolean;
  readonly indoorOutdoor?: 'indoor' | 'outdoor' | 'both' | '';
  readonly hiredBy?: StaffSource;
  readonly status: AssistantStatus;
  readonly note?: string;
  readonly addedBy?: string;
}
export type AssistantWrite = Partial<Omit<EventAssistant, '_id' | 'contact' | 'addedBy'>>;

export interface Staffing {
  readonly hiringMode?: 'facilitator' | 'wingteam' | 'mixed' | null;
  readonly requestedCount?: number | null;
  readonly notes?: string;
}
/**
 * An assistant category as the event offers it: its defaults already worked
 * out against this event's times by the API. Pay is absent for contractors.
 */
export interface EventCategoryOption {
  readonly _id: string;
  readonly name: string;
  /** Kept only so a hire carrying it shows its name; pickers leave it out. */
  readonly deleted: boolean;
  readonly role: AssistantRole;
  readonly payRate?: number | null;
  readonly payBasis?: PayBasis;
  readonly callTime?: string;
  readonly endTime?: string;
}

/** What one category's hires cost on this event (internal roles only). */
export interface CategoryPayTotal {
  /** Null for hires with no category. */
  readonly categoryId: string | null;
  readonly people: number;
  readonly total: number;
  /** Hires with no rate, or paid by the hour without both times. */
  readonly unpriced: number;
}

export interface AssistantsView {
  readonly assistants: readonly EventAssistant[];
  readonly staffing: Staffing;
  readonly categories?: readonly EventCategoryOption[];
  readonly payTotals?: readonly CategoryPayTotal[];
}

/** The hire fields a category fills on the form. */
export interface CategoryFill {
  readonly categoryId: string;
  readonly role: AssistantRole;
  readonly callTime: string;
  readonly endTime: string;
  /** Present only when the category carries pay and the viewer may see it. */
  readonly payRate?: string;
  readonly payBasis?: PayBasis;
}

/**
 * What picking a category puts in the hire form: its role, its times for this
 * event (blank when the event has no time to count from), and its pay. Every
 * field stays editable afterward.
 *
 * @param category - The category, as the event offers it.
 * @returns The form values.
 */
export function categoryFill(category: EventCategoryOption): CategoryFill {
  return {
    categoryId: category._id,
    role: category.role,
    callTime: category.callTime ?? '',
    endTime: category.endTime ?? '',
    ...(category.payRate === null || category.payRate === undefined ? {} : { payRate: String(category.payRate) }),
    ...(category.payBasis ? { payBasis: category.payBasis } : {}),
  };
}

/** One person for a bulk add, by either kind of id. */
export type PersonRef = { readonly staffContactId: string } | { readonly userId: string };

export interface BulkAssistantsWrite {
  readonly categoryId?: string;
  readonly role?: AssistantRole;
  readonly people: readonly PersonRef[];
  readonly hiredBy?: StaffSource;
  readonly status?: AssistantStatus;
}

/** The view after a bulk add, with how many were added and how many were already on the event. */
export interface BulkAssistantsResult extends AssistantsView {
  readonly added: number;
  readonly skipped: number;
}

/** The most people one bulk add takes; the API holds the same limit. */
export const MAX_BULK_ASSISTANTS = 100;

/** A category as Settings manages it. */
export interface AssistantCategory {
  readonly _id: string;
  readonly name: string;
  readonly role: AssistantRole;
  readonly payRate: number | null;
  readonly payBasis: PayBasis | null;
  readonly callOffsetMinutes: number | null;
  readonly endOffsetMinutes: number | null;
}
export type AssistantCategoryWrite = Omit<AssistantCategory, '_id'>;

export async function listAssistantCategories(signal?: AbortSignal): Promise<readonly AssistantCategory[]> {
  return getJson<AssistantCategory[]>('/assistant-categories', undefined, signal);
}
export async function createAssistantCategory(body: AssistantCategoryWrite): Promise<AssistantCategory> {
  return sendJson<AssistantCategory>('post', '/assistant-categories', body);
}
export async function updateAssistantCategory(id: string, body: Partial<AssistantCategoryWrite>): Promise<AssistantCategory> {
  return sendJson<AssistantCategory>('patch', `/assistant-categories/${id}`, body);
}
export async function deleteAssistantCategory(id: string): Promise<void> {
  await sendJson('delete', `/assistant-categories/${id}`);
}

export async function listStaffContacts(search?: string, signal?: AbortSignal): Promise<readonly StaffContact[]> {
  return getJson<StaffContact[]>('/staff-contacts', search ? { search } : undefined, signal);
}
/** A match from the duplicate guard: a staff contact, or a team member with a login. */
export interface SimilarPerson {
  readonly _id: string;
  readonly kind: PersonKind;
  readonly name: string;
  readonly phone?: string;
  readonly email?: string;
  readonly city?: string;
  readonly role?: string;
}
/** Everyone who can be an assistant: staff contacts and the team with logins, sorted by name. */
export async function listPeople(signal?: AbortSignal): Promise<readonly SimilarPerson[]> {
  return getJson<SimilarPerson[]>('/staff-contacts', { people: '1' }, signal);
}
/** The duplicate guard: people who look like the one being created, by name, phone or email, from the directory and the team. */
export async function similarStaffContacts(query: { readonly name?: string; readonly phone?: string; readonly email?: string }, signal?: AbortSignal): Promise<readonly SimilarPerson[]> {
  return getJson<SimilarPerson[]>(
    '/staff-contacts',
    { ...(query.name ? { similar: query.name } : {}), ...(query.phone ? { phone: query.phone } : {}), ...(query.email ? { email: query.email } : {}) },
    signal,
  );
}
export async function readStaffContact(id: string, signal?: AbortSignal): Promise<StaffContact> {
  return getJson<StaffContact>(`/staff-contacts/${id}`, undefined, signal);
}
export interface StaffContactEvent {
  readonly _id: string;
  readonly contractNumber?: string;
  readonly opportunityName?: string;
  readonly eventDate?: string;
  readonly programName?: string;
  readonly city?: string;
  readonly state?: string;
  readonly role?: AssistantRole;
  readonly status?: AssistantStatus;
}
export async function listStaffContactEvents(id: string, signal?: AbortSignal): Promise<readonly StaffContactEvent[]> {
  return getJson<StaffContactEvent[]>(`/staff-contacts/${id}/events`, undefined, signal);
}
export async function createStaffContact(body: StaffContactWrite): Promise<StaffContact> {
  return sendJson<StaffContact>('post', '/staff-contacts', body);
}
export async function updateStaffContact(id: string, body: Partial<StaffContactWrite>): Promise<StaffContact> {
  return sendJson<StaffContact>('patch', `/staff-contacts/${id}`, body);
}
export async function deleteStaffContact(id: string): Promise<void> {
  await sendJson('delete', `/staff-contacts/${id}`);
}

export async function listAssistants(eventId: string, signal?: AbortSignal): Promise<AssistantsView> {
  const response = await axios.get<{ data: AssistantsView }>(`/api/v3/events/${eventId}/assistants`, { ...(signal ? { signal } : {}), headers: await authHeader() });
  return response.data.data;
}
export async function addAssistant(eventId: string, body: AssistantWrite): Promise<AssistantsView> {
  const response = await axios.post<{ data: AssistantsView }>(`/api/v3/events/${eventId}/assistants`, body, { headers: await authHeader() });
  return response.data.data;
}
export async function bulkAddAssistants(eventId: string, body: BulkAssistantsWrite): Promise<BulkAssistantsResult> {
  const response = await axios.post<{ data: BulkAssistantsResult }>(`/api/v3/events/${eventId}/assistants/bulk`, body, { headers: await authHeader() });
  return response.data.data;
}
export async function updateAssistant(eventId: string, assistantId: string, body: Partial<AssistantWrite>): Promise<AssistantsView> {
  const response = await axios.patch<{ data: AssistantsView }>(`/api/v3/events/${eventId}/assistants/${assistantId}`, body, { headers: await authHeader() });
  return response.data.data;
}
export async function removeAssistant(eventId: string, assistantId: string): Promise<AssistantsView> {
  const response = await axios.delete<{ data: AssistantsView }>(`/api/v3/events/${eventId}/assistants/${assistantId}`, { headers: await authHeader() });
  return response.data.data;
}
export async function saveStaffing(eventId: string, body: Staffing): Promise<Staffing> {
  const response = await axios.put<{ data: Staffing }>(`/api/v3/events/${eventId}/staffing`, body, { headers: await authHeader() });
  return response.data.data;
}

export function assistantStatusTone(status: AssistantStatus): 'success' | 'warning' | 'neutral' {
  return status === 'confirmed' ? 'success' : status === 'requested' ? 'warning' : 'neutral';
}

const MINUTES_PER_HOUR = 60;

/** Which end of the event a category's offset counts from. */
export const OFFSET_EDGE = { start: 'start', end: 'end' } as const;
export type OffsetEdge = (typeof OFFSET_EDGE)[keyof typeof OFFSET_EDGE];

/**
 * A category's offset the way a person says it: "1 hr 30 min before the
 * start", "At the end", or null when the category sets no time.
 *
 * @param minutes - Minutes before the start, or after the end.
 * @param edge - Which end of the event it counts from.
 * @returns The phrase, or null.
 */
export function describeOffset(minutes: number | null | undefined, edge: OffsetEdge): string | null {
  if (minutes === null || minutes === undefined) return null;
  if (minutes === 0) return edge === OFFSET_EDGE.start ? 'At the start' : 'At the end';
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  const span = [hours > 0 ? `${hours} hr` : null, rest > 0 ? `${rest} min` : null].filter(Boolean).join(' ');
  return edge === OFFSET_EDGE.start ? `${span} before the start` : `${span} after the end`;
}
